import { Component } from '@theme/component';
import { debounce, preventDefault } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/** Distance (in px) the pointer must travel before the gesture is treated as a drag. */
const DRAG_THRESHOLD = 5;

/** Velocity decay applied on every 60fps frame once the pointer is released. */
const MOMENTUM_DECAY = 0.94;

/** Velocity (in px/ms) under which the momentum is considered finished. */
const MOMENTUM_MIN_VELOCITY = 0.05;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);

    // Allow the visitor to scrub the marquee left/right with touch (or a mouse drag).
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
    // Suppress the click that follows a drag so marquee links aren't triggered by mistake.
    this.addEventListener('click', this.#handleClick, { capture: true });
    this.addEventListener('dragstart', preventDefault);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);

    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);
    this.removeEventListener('click', this.#handleClick, { capture: true });
    this.removeEventListener('dragstart', preventDefault);

    this.#stopMomentum();
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  /**
   * The pointer currently interacting with the marquee.
   * @type {{ id: number, type: string, startX: number, lastX: number, lastTime: number, velocity: number, dragging: boolean } | null}
   */
  #pointer = null;

  /**
   * The `requestAnimationFrame` id of the momentum loop.
   * @type {number | null}
   */
  #momentumFrame = null;

  /**
   * Whether the current interaction turned into a drag, used to swallow the trailing click.
   * @type {boolean}
   */
  #dragged = false;

  /**
   * Whether the marquee animation is currently paused by a drag.
   * @type {boolean}
   */
  #paused = false;

  /**
   * Manual offset (in px) applied to the wrapper when no CSS animation is running,
   * e.g. when the visitor prefers reduced motion.
   * @type {number}
   */
  #manualOffset = 0;

  /**
   * The CSS animation driving the marquee, if any.
   * @returns {Animation | null}
   */
  get #marqueeAnimation() {
    return this.refs.wrapper.getAnimations()[0] ?? null;
  }

  /**
   * The distance (in px) the wrapper travels before the marquee loops back to its initial position,
   * cached for the duration of a gesture to avoid a forced layout on every frame.
   * @type {number | null}
   */
  #loopDistance = null;

  /**
   * Whether the marquee animation runs backwards, cached alongside the loop distance.
   * @type {boolean}
   */
  #reversed = false;

  /**
   * Whether the wrapper uses the RTL keyframe (`marquee-motion-rtl`), where a forward
   * timeline moves the content to the right instead of the left.
   * @type {boolean}
   */
  #rtlMotion = false;

  /**
   * Reads the geometry the drag relies on, matching the `marquee-motion` keyframe:
   * `translate3d(calc(-50% - (var(--marquee-gap) / 2)), 0, 0)`.
   */
  #measure() {
    const { wrapper } = this.refs;
    const styles = getComputedStyle(wrapper);
    const gap = parseFloat(styles.columnGap) || 0;

    // The wrapper holds the content and its clone, so a full loop is half its width plus half a gap.
    this.#loopDistance = wrapper.offsetWidth / 2 + gap / 2;
    this.#reversed = styles.animationDirection === 'reverse';
    // In RTL the keyframe translates to +50%, and with no CSS animation (reduced motion)
    // the manual offset must stay positive because the wrapper overflows to the left.
    this.#rtlMotion =
      styles.animationName === 'marquee-motion-rtl' ||
      (styles.animationName === 'none' && styles.direction === 'rtl');
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (!event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#stopMomentum();
    this.#dragged = false;

    this.#pointer = {
      id: event.pointerId,
      type: event.pointerType,
      startX: event.clientX,
      lastX: event.clientX,
      lastTime: event.timeStamp,
      velocity: 0,
      dragging: false,
    };
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    const pointer = this.#pointer;

    if (!pointer || event.pointerId !== pointer.id) return;

    if (!pointer.dragging) {
      if (Math.abs(event.clientX - pointer.startX) < DRAG_THRESHOLD) return;

      pointer.dragging = true;
      this.#dragged = true;
      this.dataset.dragging = 'true';
      // Measure once per gesture so the frames that follow don't force a layout.
      this.#measure();
      this.#pauseAnimation();

      // The browser may have started a text selection before `user-select: none` applied.
      if (pointer.type === 'mouse') document.getSelection()?.removeAllRanges();

      try {
        this.setPointerCapture(pointer.id);
      } catch {
        // Ignore browsers refusing the capture, the listeners on `this` still receive the events.
      }
    }

    const deltaX = event.clientX - pointer.lastX;
    const elapsed = event.timeStamp - pointer.lastTime;

    if (elapsed > 0) pointer.velocity = deltaX / elapsed;

    pointer.lastX = event.clientX;
    pointer.lastTime = event.timeStamp;

    this.#scrollBy(deltaX);

    // Avoid text selection and native image dragging while scrubbing.
    event.preventDefault();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    const pointer = this.#pointer;

    if (!pointer || event.pointerId !== pointer.id) return;

    this.#pointer = null;

    if (this.hasPointerCapture(pointer.id)) this.releasePointerCapture(pointer.id);

    if (!pointer.dragging) {
      // A tap that interrupted the momentum still has to release the paused animation.
      if (this.#paused) this.#resumeAnimation(pointer.type);
      return;
    }

    delete this.dataset.dragging;

    const velocity = event.type === 'pointercancel' ? 0 : pointer.velocity;

    this.#startMomentum(velocity, () => this.#resumeAnimation(pointer.type));
  };

  /**
   * Swallows the click triggered at the end of a drag so marquee links aren't followed by mistake.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!this.#dragged) return;

    this.#dragged = false;
    event.preventDefault();
    event.stopPropagation();
  };

  #pauseAnimation() {
    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;
    this.#paused = true;

    const animation = this.#marqueeAnimation;

    if (!animation) return;

    // Set the rate synchronously (instead of `updatePlaybackRate`) so the drag isn't
    // fighting a leftover hover slowdown.
    animation.playbackRate = 1;
    animation.pause();
  }

  /**
   * @param {string} pointerType - The type of pointer that initiated the drag.
   */
  #resumeAnimation(pointerType) {
    this.#paused = false;

    const animation = this.#marqueeAnimation;

    if (!animation) return;

    animation.play();

    // A mouse never leaves the element after a drag, so re-apply the hover slowdown.
    if (pointerType === 'mouse' && this.matches(':hover')) this.#slowDown();
  }

  /**
   * Moves the marquee horizontally by the given amount of pixels.
   * @param {number} deltaX - The distance to move, positive values move the content to the right.
   */
  #scrollBy(deltaX) {
    if (!deltaX) return;

    if (this.#loopDistance === null) this.#measure();

    const loopDistance = this.#loopDistance;

    if (!loopDistance || loopDistance <= 0) return;

    const animation = this.#marqueeAnimation;

    if (animation) {
      const duration = Number(animation.effect?.getComputedTiming().duration) || 0;

      if (!duration) return;

      // With `animation-direction: reverse` the timeline runs backwards, so does the time delta.
      // In RTL the keyframe moves the content right on a forward timeline, so the sign flips.
      const directionSign = this.#rtlMotion ? 1 : this.#reversed ? 1 : -1;
      const timeDelta = (directionSign * deltaX * duration) / loopDistance;
      const currentTime = Number(animation.currentTime) || 0;

      // Every iteration is identical, so wrapping the time keeps the loop seamless.
      animation.currentTime = modulo(currentTime + timeDelta, duration);
      return;
    }

    // No CSS animation (e.g. reduced motion): translate the wrapper manually.
    if (this.#rtlMotion) {
      // In RTL the wrapper overflows to the left, so the offset stays positive (0 → loopDistance).
      this.#manualOffset = modulo(this.#manualOffset + deltaX, loopDistance);
    } else {
      this.#manualOffset = modulo(this.#manualOffset + deltaX, loopDistance) - loopDistance;
    }
    this.refs.wrapper.style.translate = `${this.#manualOffset}px`;
  }

  /**
   * Keeps the marquee moving after the pointer is released, then runs the callback.
   * @param {number} velocity - The release velocity in px/ms.
   * @param {() => void} onComplete - Called once the momentum has settled.
   */
  #startMomentum(velocity, onComplete) {
    if (Math.abs(velocity) < MOMENTUM_MIN_VELOCITY) return onComplete();

    let currentVelocity = velocity;
    let lastTime = performance.now();

    /** @param {number} time */
    const step = (time) => {
      const elapsed = Math.max(time - lastTime, 0);
      lastTime = time;

      this.#scrollBy(currentVelocity * elapsed);

      // Normalize the decay so it doesn't depend on the display refresh rate.
      currentVelocity *= MOMENTUM_DECAY ** (elapsed / (1000 / 60));

      if (Math.abs(currentVelocity) < MOMENTUM_MIN_VELOCITY) {
        this.#momentumFrame = null;
        onComplete();
        return;
      }

      this.#momentumFrame = requestAnimationFrame(step);
    };

    this.#momentumFrame = requestAnimationFrame(step);
  }

  #stopMomentum() {
    if (this.#momentumFrame === null) return;

    cancelAnimationFrame(this.#momentumFrame);
    this.#momentumFrame = null;
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();

    // The wrapper geometry changed, so the cached drag measurements are stale.
    this.#loopDistance = null;
    this.#manualOffset = 0;
    this.refs.wrapper.style.removeProperty('translate');
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

/**
 * Returns the euclidean remainder, so the result is always positive.
 * @param {number} value - The value to wrap.
 * @param {number} length - The length to wrap the value around.
 * @returns {number} The wrapped value, between 0 and `length`.
 */
function modulo(value, length) {
  return ((value % length) + length) % length;
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
